/**
 * ProductSection — Reusable section displaying a titled product grid.
 *
 * Swiss design: clean heading hierarchy, generous padding, grid alignment.
 * Used for "New Products" and "Best Sellers" homepage sections.
 */
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ProductGrid } from "@modules/shop/components/ProductCard";
import {
  SectionHeader,
  SectionLinkLabel,
  sectionLinkClass,
} from "./SectionHeader";
import type { CurrencyCode } from "@shopify/hydrogen-react/storefront-api-types";

interface Product {
  id: string;
  title: string;
  handle: string;
  availableForSale: boolean;
  featuredImage?: {
    url?: string;
    altText?: string | null;
  } | null;
  priceRange: {
    minVariantPrice: {
      amount: string;
      currencyCode: CurrencyCode;
    };
  };
  compareAtPriceRange?: {
    minVariantPrice?: {
      amount?: string;
      currencyCode?: CurrencyCode;
    } | null;
  } | null;
}

interface ProductSectionProps {
  /** Section heading text */
  title: string;
  /** "View All" link label */
  viewAllLabel: string;
  /** Link destination for "View All" */
  viewAllHandle?: string;
  locale: string;
  products: Array<Product>;
}

export function ProductSection({
  title,
  viewAllLabel,
  viewAllHandle,
  locale,
  products,
}: Readonly<ProductSectionProps>) {
  const { t } = useTranslation();

  return (
    <section className="px-4 py-14 sm:px-6 lg:px-8 max-mobile:py-10">
      <div className="mx-auto max-w-7xl">
        <SectionHeader
          title={title}
          link={
            viewAllHandle ? (
              <Link
                to="/$locale/collections/$handle"
                params={{ locale, handle: viewAllHandle }}
                className={sectionLinkClass}
              >
                <SectionLinkLabel label={viewAllLabel} />
              </Link>
            ) : (
              <Link
                to="/$locale/products"
                params={{ locale }}
                className={sectionLinkClass}
              >
                <SectionLinkLabel label={viewAllLabel} />
              </Link>
            )
          }
        />
        {products.length > 0 ? (
          <ProductGrid products={products} locale={locale} columns={4} />
        ) : (
          <p className="text-text-3">{t("Homepage.noProducts")}</p>
        )}
      </div>
    </section>
  );
}
